import { useState } from 'react';
import { BlockWrapper, defineBlock, smartProps } from '@smartfire/js-block-engine';
import { EditableText } from '@smartfire/components';
import { Collapse, useMediaQuery } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import FacebookIcon from '../assets/icons/facebook';
import InstagramIcon from '../assets/icons/instagram';
import PinterestIcon from '../assets/icons/pinterest';
import he from 'he';
import { ReactComponent as Logo } from '../assets/logo_white.svg';

import style from './style.module.scss';

const FooterBlock = ({ logo, copyright, mainMenu, customSettings }) => {
    const { facebookUrl, instagramUrl, pinterestUrl } = customSettings ? customSettings : {};
    const isMobile = useMediaQuery(`(max-width:${style.mobileBreakpoint})`);

    const socialsData = [
        { name: 'Facebook', url: facebookUrl, icon: <FacebookIcon /> },
        { name: 'Instagram', url: instagramUrl, icon: <InstagramIcon /> },
        { name: 'Pinterest', url: pinterestUrl, icon: <PinterestIcon /> },
    ];

    return (
        <BlockWrapper.Footer className={style.footer}>
            <Logo className={style.logo} />
            <ul className={style.socials}>
                {socialsData.map(
                    (social) =>
                        social.url && (
                            <li key={social.name}>
                                <a href={social.url} title={social.name} target="_blank" rel="noreferrer">
                                    {social.icon}
                                </a>
                            </li>
                        ),
                )}
            </ul>
            {mainMenu && (
                <nav className={style.menu}>
                    {isMobile ? <MobileMenu menu={mainMenu} /> : <DesktopMenu menu={mainMenu} />}
                </nav>
            )}
            <EditableText tagName="p" smartProp="copyright" placeholder="Copyright" className={style.copyright} />
        </BlockWrapper.Footer>
    );
};

const DesktopMenu = ({ menu }) => {
    return menu ? (
        <ul className={style.desktopMenu}>
            {menu.map((menuItem) => {
                if (menuItem.children?.length > 0) {
                    const Tag = menuItem.url !== '#' ? 'a' : 'span';
                    const tagProps = menuItem.url !== '#' ? { href: menuItem.url } : {};
                    return (
                        <li className={style.itemList} key={'footer_desktop_mainmenu_' + menuItem.ID}>
                            <Tag className={style.name} {...tagProps}>
                                {he.decode(menuItem.title)}
                            </Tag>
                            <ul>
                                {menuItem.children.map((menuItem2) => (
                                    <li key={'footer_desktop_mainmenu_sub_' + menuItem2.ID}>
                                        <a href={menuItem2.url}>{he.decode(menuItem2.title)}</a>
                                    </li>
                                ))}
                            </ul>
                        </li>
                    );
                } else {
                    return (
                        <li className={style.item} key={'footer_desktop_mainmenu_' + menuItem.ID}>
                            <a href={menuItem.url}>{he.decode(menuItem.title)}</a>
                        </li>
                    );
                }
            })}
        </ul>
    ) : null;
};

const MobileMenu = ({ menu }) => {
    return menu ? (
        <ul className={style.mobileMenu}>
            {menu.map((menuItem) => {
                if (menuItem.children?.length > 0) {
                    return <MobileMenuItem key={'footer_mobile_mainmenu_' + menuItem.ID} menuItem={menuItem} />;
                } else {
                    return (
                        <li className={style.item} key={'footer_mobile_mainmenu_' + menuItem.ID}>
                            <a href={menuItem.url}>{he.decode(menuItem.title)}</a>
                        </li>
                    );
                }
            })}
        </ul>
    ) : null;
};

const MobileMenuItem = ({ menuItem }) => {
    const [open, setOpen] = useState(false);

    const handleClick = () => {
        setOpen((prev) => !prev);
    };

    return (
        <li className={style.itemList} key={'footer_mobile_mainmenu_' + menuItem.ID}>
            <span className={style.name} onClick={handleClick}>
                {menuItem.title}
                {open ? <ExpandLess /> : <ExpandMore />}
            </span>
            <Collapse in={open}>
                <ul>
                    {menuItem.children.map((menuItem2) => (
                        <li key={'footer_mobile_mainmenu_sub_' + menuItem2.ID}>
                            <a href={menuItem2.url}>{he.decode(menuItem2.title)}</a>
                        </li>
                    ))}
                </ul>
            </Collapse>
        </li>
    );
};

export default defineBlock({
    Block: FooterBlock,
    smartProps: {
        copyright: smartProps.string.withDefault(''),
        languages: smartProps.array.contentServerSide(),
        activeLang: smartProps.string.contentServerSide(),
        homeUrl: smartProps.string.contentServerSide(),
        mainMenu: smartProps.array.contentServerSide(),
        customSettings: smartProps.object.contentServerSide(),
    },
    setup: {
        title: 'Footer',
    },
});
